import { loadOwnedFullGame } from './ownedFullGames'
import { type GameHistoryEntry, type UserGamesSummary } from './userModel'

export type PendingGameResult = {
  id: string
  kind: 'strategy' | 'puzzle'
  vsAI: boolean
  result: 'win' | 'loss' | 'draw'
  ts: number
  scoreDiff?: number
  durationMs?: number
  statsRecorded?: boolean
}
export type GameResultsOutbox = Record<string, PendingGameResult[]>
export type GameStatsSnapshot = { rating: number; games: UserGamesSummary }

export function pendingResultFromHistory(
  entry: GameHistoryEntry,
  recoverTime = false,
): PendingGameResult | null {
  if (!entry.id || !entry.result) return null
  let durationMs = entry.durationMs
  if (recoverTime && durationMs == null && entry.vsAI) {
    const full = entry.fullGameRef
      ? loadOwnedFullGame(entry.fullGameRef, entry.fullGameRef.ownerKey)
      : null
    const players = Array.isArray(full?.players)
      ? (full.players as Array<{ name?: string; timeMs?: number }>)
      : []
    const me =
      typeof full?.meIndex === 'number'
        ? players[full.meIndex]
        : players.find((player) => entry.myName && player.name === entry.myName)
    durationMs = me?.timeMs
  }
  const scoreDiff =
    entry.scoreFor != null && entry.scoreAgainst != null
      ? entry.scoreFor - entry.scoreAgainst
      : undefined
  const hasMetrics =
    entry.vsAI &&
    entry.kind !== 'puzzle' &&
    scoreDiff != null &&
    Number.isInteger(scoreDiff) &&
    Math.abs(scoreDiff) <= 2147483647 &&
    durationMs != null &&
    Number.isFinite(durationMs) &&
    durationMs > 0 &&
    durationMs <= 2147483647
  return {
    id: entry.id,
    kind: entry.kind === 'puzzle' ? 'puzzle' : 'strategy',
    vsAI: !!entry.vsAI,
    result: entry.result,
    ts: entry.ts,
    ...(hasMetrics
      ? { scoreDiff, durationMs: Math.max(1, Math.round(durationMs!)) }
      : {}),
  }
}

export function applyGameResult(
  snapshot: GameStatsSnapshot,
  result: PendingGameResult,
): GameStatsSnapshot {
  if (result.statsRecorded) return snapshot
  const games = { ...snapshot.games, total: (snapshot.games.total ?? 0) + 1 }
  let rating = snapshot.rating
  if (result.kind === 'puzzle') {
    games.puzzle = {
      played: (games.puzzle?.played ?? 0) + 1,
      completed: (games.puzzle?.completed ?? 0) + 1,
    }
  } else {
    const key = result.vsAI ? 'vsAI' : 'vsPlayers'
    const counters = { played: 0, wins: 0, losses: 0, draws: 0, ...games[key] }
    counters.played++
    if (result.result === 'win') counters.wins++
    else if (result.result === 'loss') counters.losses++
    else counters.draws++
    games[key] = counters
    const delta =
      result.result === 'win'
        ? result.vsAI
          ? 12
          : 18
        : result.result === 'loss'
          ? result.vsAI
            ? -8
            : -12
          : 3
    rating = Math.max(800, Math.min(2200, rating + delta))
  }
  return { rating, games }
}
